import React from "react";
import { useTranslation } from "react-i18next";
import { RoomChatState } from "./chatTypes";
import { cn } from "../../../lib/utils";
import { LogOut } from "lucide-react";
import { SentimentIndicator, SentimentType } from "./SentimentIndicator";
import { useHotelTimezone } from "../../../contexts/HotelTimezoneContext";

interface ChatConversationItemProps {
  roomId: string;
  chat: RoomChatState;
  isSelected: boolean;
  onSelect: (roomId: string) => void;
  formatTime: (date: Date) => string;
  sentiment?: SentimentType;
  hasUnread?: boolean;
}

export const ChatConversationItem: React.FC<ChatConversationItemProps> = ({
  roomId,
  chat,
  isSelected,
  onSelect,
  formatTime,
  sentiment,
  hasUnread = false,
}) => {
  const { t, i18n } = useTranslation();
  const hotelTimezone = useHotelTimezone();
  const lastMessage = chat.messages[chat.messages.length - 1];
  const hasRoomNumber = !!chat.actualRoomNumber;
  const displayRoomNumber = chat.actualRoomNumber || t("adminChatPage.unknownRoom", "?");
  const unreadCount = chat.unreadCount || 0;

  // Format relative time for better UX
  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("adminChatPage.justNow", "now");
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return formatTime(date);
  };

  // Format checkout date indicator
  const getCheckoutIndicator = (
    checkoutDate: Date | undefined
  ): { text: string; isUrgent: boolean } | null => {
    if (!checkoutDate) return null;

    const todayStr = new Intl.DateTimeFormat("en-CA", {
      timeZone: hotelTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());

    const checkoutStr = new Intl.DateTimeFormat("en-CA", {
      timeZone: hotelTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(checkoutDate);

    const todayDate = new Date(todayStr + "T00:00:00");
    const checkoutDateNorm = new Date(checkoutStr + "T00:00:00");
    const diffDays = Math.floor(
      (checkoutDateNorm.getTime() - todayDate.getTime()) / 86400000
    );

    if (diffDays < 0) return null;
    if (diffDays === 0)
      return { text: t("adminChatPage.checkoutToday", "Today"), isUrgent: true };
    if (diffDays === 1)
      return {
        text: t("adminChatPage.checkoutTomorrow", "Tomorrow"),
        isUrgent: false,
      };

    const locale = i18n.language || "en";
    const formatted = new Intl.DateTimeFormat(locale, {
      timeZone: hotelTimezone,
      month: "short",
      day: "numeric",
    }).format(checkoutDate);
    return { text: formatted, isUrgent: false };
  };

  const checkoutIndicator = getCheckoutIndicator(chat.checkoutDate);
  // Only show guest name if it's a real name, not just "Guest" fallback
  const hasRealGuestName = chat.guestName && chat.guestName.trim().length > 0;

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(roomId);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(roomId)}
      onKeyDown={handleKeyDown}
      aria-selected={isSelected}
      aria-label={`${hasRoomNumber ? `${t("adminChatPage.roomLabel", "Room")} ${displayRoomNumber}` : hasRealGuestName ? chat.guestName : t("adminChatPage.guestChat", "Guest Chat")}${unreadCount > 0 ? `, ${unreadCount} ${t("adminChatPage.unreadMessages", "unread messages")}` : ''}`}
      data-testid={`admin-chat-conversation-${displayRoomNumber}`}
      data-room-number={displayRoomNumber}
      data-room-token={roomId}
      className={cn(
        "px-3 py-2.5 border-b cursor-pointer transition-all duration-150",
        "hover:bg-accent/50 focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-inset",
        isSelected && "bg-accent border-l-2 border-l-primary",
        hasUnread && !isSelected && "bg-destructive/5"
      )}
    >
      <div className="flex items-start gap-2.5">
        {/* Avatar/Room indicator */}
        <div
          className={cn(
            "shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium",
            hasUnread
              ? "bg-destructive/10 text-destructive"
              : "bg-muted text-muted-foreground"
          )}
        >
          {hasRoomNumber
            ? displayRoomNumber.slice(0, 3)
            : hasRealGuestName
              ? chat.guestName!.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()
              : "?"}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Room number + time + unread */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={cn(
                  "font-medium text-sm truncate",
                  hasUnread && "text-foreground"
                )}
              >
                {hasRoomNumber
                  ? `${t("adminChatPage.roomLabel", "Room")} ${displayRoomNumber}`
                  : hasRealGuestName
                    ? chat.guestName
                    : t("adminChatPage.guestChat", "Guest Chat")}
              </span>
              {sentiment && (
                <SentimentIndicator sentiment={sentiment} size="sm" />
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {lastMessage && (
                <span className="text-[11px] text-muted-foreground">
                  {getRelativeTime(lastMessage.timestampUtc)}
                </span>
              )}
              {unreadCount > 0 && (
                <span
                  data-testid={`admin-chat-unread-${displayRoomNumber}`}
                  className={cn(
                    "inline-flex items-center justify-center",
                    "min-w-[18px] h-[18px] px-1 rounded-full",
                    "text-[10px] font-semibold",
                    "bg-destructive text-destructive-foreground"
                  )}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          </div>

          {/* Row 2: Guest name and/or checkout indicator (simplified) */}
          {/* Skip guest name here if already shown as primary label (no room number) */}
          {((hasRealGuestName && hasRoomNumber) || checkoutIndicator) && (
            <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
              {hasRealGuestName && hasRoomNumber && (
                <span className="truncate">{chat.guestName}</span>
              )}
              {hasRealGuestName && hasRoomNumber && checkoutIndicator && (
                <span className="text-muted-foreground/50">·</span>
              )}
              {checkoutIndicator && (
                <div
                  className={cn(
                    "flex items-center gap-0.5 shrink-0",
                    checkoutIndicator.isUrgent
                      ? "text-orange-500"
                      : "text-muted-foreground"
                  )}
                >
                  <LogOut className="h-3 w-3" />
                  <span>{checkoutIndicator.text}</span>
                </div>
              )}
            </div>
          )}

          {/* Row 3: Last message preview */}
          {lastMessage && (
            <p
              className={cn(
                "text-xs truncate mt-0.5",
                hasUnread ? "text-foreground/80" : "text-muted-foreground"
              )}
            >
              {lastMessage.isStaffMessage && (
                <span className="text-muted-foreground">
                  {t("adminChatPage.you", "You")}:{" "}
                </span>
              )}
              {lastMessage.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
